import {
  AppShell,
  AppShellMainProps,
  AppShellNavbarProps,
  AppShellProps,
} from '@mantine/core';
import { useMobileDetector } from '@zorro/web/ui-utils/hooks/useMobileDetector';
import { ReactElement, ReactNode } from 'react';

import { ZorroBox } from '../Box';
import { brand } from '../theme';
import classNames from './AppShell.module.scss';
import { ImpersonationIndicator } from './ImpersonationIndicator';

export const AppShellConstants = {
  NAVBAR_WIDTH_EXPANDED: 272,
  NAVBAR_VERTICAL_OFFSET: 100,
  HEADER_HEIGHT: 80,
  IMPERSONATION_INDICATOR_HEIGHT: 28,
  DASHBOARD_LAYOUT_PADDING: 16,
  APP_SHELL_PADDING: 16,
} as const;

export interface IAppShellProps extends AppShellProps {
  children: ReactNode;
  headerComponent?: ReactElement;
  navbarComponent?: ReactElement;
  navbarProps?: AppShellNavbarProps;
  mainProps?: AppShellMainProps;
  hasNavbarBorder?: boolean;
  impersonatedEmployeeFullName?: string | null;
}

/**
 * @deprecated Use the new {@link AppLayout} instead.
 */
export function ZorroAppShell({
  children,
  headerComponent,
  navbarComponent,
  navbarProps,
  navbar,
  mainProps,
  hasNavbarBorder = true,
  impersonatedEmployeeFullName,
  ...props
}: IAppShellProps) {
  const { isMobile } = useMobileDetector();

  return (
    <AppShell
      classNames={classNames}
      padding={
        impersonatedEmployeeFullName
          ? AppShellConstants.HEADER_HEIGHT +
            AppShellConstants.IMPERSONATION_INDICATOR_HEIGHT
          : AppShellConstants.HEADER_HEIGHT
      }
      navbar={{
        width: { sm: AppShellConstants.NAVBAR_WIDTH_EXPANDED },
        collapsed: { mobile: true, desktop: navbarComponent === undefined },
        breakpoint: 'sm',
        ...navbar,
      }}
      {...props}
    >
      {headerComponent && (
        <AppShell.Header
          h={
            impersonatedEmployeeFullName
              ? AppShellConstants.HEADER_HEIGHT +
                AppShellConstants.IMPERSONATION_INDICATOR_HEIGHT
              : AppShellConstants.HEADER_HEIGHT
          }
          zIndex={200}
          style={(theme) => ({
            borderBottom: `1px solid ${theme.colors.zorroGray[6]}`,
          })}
        >
          {impersonatedEmployeeFullName && (
            <ImpersonationIndicator
              impersonatedEmployeeFullName={impersonatedEmployeeFullName}
            />
          )}
          <ZorroBox
            px="md"
            py={
              impersonatedEmployeeFullName
                ? AppShellConstants.IMPERSONATION_INDICATOR_HEIGHT + 12
                : 'sm'
            }
          >
            {headerComponent}
          </ZorroBox>
        </AppShell.Header>
      )}
      <AppShell.Navbar
        pt={
          impersonatedEmployeeFullName
            ? AppShellConstants.NAVBAR_VERTICAL_OFFSET +
              AppShellConstants.IMPERSONATION_INDICATOR_HEIGHT
            : AppShellConstants.NAVBAR_VERTICAL_OFFSET
        }
        bg={brand.zorroGray50}
        miw={80}
        style={(theme) => ({
          borderRight: hasNavbarBorder
            ? `1px solid ${theme.colors.zorroGray[6]}`
            : 'unset',
        })}
        h="100%"
        {...navbarProps}
      >
        {navbarComponent}
      </AppShell.Navbar>
      <AppShell.Main px={isMobile ? 'sm' : undefined} {...mainProps}>
        {children}
      </AppShell.Main>
    </AppShell>
  );
}
